/** Lifecycle state of a provider pull request attached to a workspace. */
export type PullRequestState = 'draft' | 'open' | 'merged' | 'closed';

/** Whether the provider can merge the pull request without resolving conflicts first. */
export type PullRequestMergeability = 'mergeable' | 'conflicting' | 'unknown';

/** Where a pull request's CI checks stand on its head: one failed, some still run, all passed. */
export type PullRequestChecksState = 'failing' | 'pending' | 'passing';

/** Compact pull request summary embedded in a workspace git status. */
export type PullRequestSummary = {
  url: string;
  number: number;
  title: string;
  body: string;
  state: PullRequestState;
  mergeability?: PullRequestMergeability;
  /** Its checks on its head, when the forge said (an open or draft pull request's). */
  checks?: PullRequestChecksState;
  /** Where its reviews stand, when the forge said (a pull request read in full). */
  reviewDecision?: 'approved' | 'changes_requested' | 'review_required' | 'unknown';
  /** Lines it adds and removes, when the forge said. */
  additions?: number;
  deletions?: number;
  baseBranch: string;
  headBranch: string;
  updatedAt: string | null;
};

/** Normalizes a provider's pull request flags into Crowdex's lifecycle state. */
export function pullRequestState(input: {
  draft?: boolean;
  merged?: boolean;
  state?: string;
}): PullRequestState {
  if (input.merged === true) return 'merged';
  if (input.state?.toLowerCase() === 'closed') return 'closed';
  if (input.draft === true) return 'draft';
  return 'open';
}

/** The number of a forge's pull request URL (`…/pull/7`, `…/-/merge_requests/7`), or null. */
export function pullRequestNumberFromUrl(url: string | null): number | null {
  if (url === null) return null;
  const match = url.match(/^https:\/\/[^/]+\/.+?\/(?:pull|-\/merge_requests)\/(\d+)(?:\/.*)?$/i);
  if (match?.[1] === undefined) return null;
  const number = Number(match[1]);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}
